// PickUpMatchmaking.js
// Pick Up Draft — Phase E. Web port of PickUpMatchmakingView.swift.
//
// Reached from the hub's FIND OPPONENT (?sport=). Enters the queue on arrival, shows the
// searching state with the 3-hour countdown, and auto-routes into the draft the moment the
// server pairs you. The queue persists server-side — leaving this screen does NOT cancel it;
// only CANCEL does. expireMatchmakingEntries (hourly) is the authority on expiry.
//
// Reachable at /battles/pickup/matchmaking?sport=<id>.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../managers/AuthContext";
import {
  enterMatchmakingQueue,
  cancelMatchmaking,
  listenToMatchmakingEntry,
  listenToActivePickUpGames,
} from "../managers/matchmakingService";

const APP_THEME = "#d4f53c";
const SPORTS = [
  { id: "football", label: "Football", hex: "#00b04f" },
  { id: "baseball", label: "Baseball", hex: "#db2626" },
  { id: "basketball", label: "Basketball", hex: "#ff8c00" },
  { id: "hockey", label: "Hockey", hex: "#0da6e8" },
  { id: "soccer", label: "Soccer", hex: "#1a66e6" },
];
const sportMeta = (id) => SPORTS.find((s) => s.id === id) || SPORTS[0];

function fmtRemaining(expiresAt) {
  if (!expiresAt) return "--:--:--";
  const ms = expiresAt.getTime() - Date.now();
  if (ms <= 0) return "00:00:00";
  const s = Math.floor(ms / 1000);
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

export default function PickUpMatchmaking() {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const username = user?.username || null;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sportId = searchParams.get("sport") || "football";
  const meta = sportMeta(sportId);

  const [state, setState] = useState("starting"); // starting | searching | expired | error
  const [expiresAt, setExpiresAt] = useState(null);
  const [, setTick] = useState(0);
  const navigatedRef = useRef(false);
  const enteredForRef = useRef(null); // sportId we've already entered — guards double-entry

  // Enter (or re-enter) the queue. Guarded by enteredForRef so a re-render or React
  // StrictMode's double-invoke can't fire the callable twice — that duplicate attempt was
  // what surfaced "Couldn't start matchmaking" even though the first entry succeeded and a
  // match was being made. The result is read defensively so a missing payload can't throw.
  const enterQueue = useCallback(async () => {
    if (!uid) return;
    enteredForRef.current = sportId;
    navigatedRef.current = false;
    setState("starting");
    try {
      const res = await enterMatchmakingQueue({ userID: uid, username, sportId });
      setExpiresAt(res?.expiresAt ?? null);
      setState("searching");
    } catch (_) {
      setState("error");
    }
  }, [uid, username, sportId]);

  // Enter on arrival (once per sport).
  useEffect(() => {
    if (!uid || enteredForRef.current === sportId) return;
    enterQueue();
  }, [uid, sportId, enterQueue]);

  // While searching, listen for the match and route straight into the draft. Kept in its own
  // effect so it can attach/detach freely (StrictMode-safe) without re-entering the queue:
  // onSnapshot delivers the current entry on attach, so attaching late still catches a match.
  useEffect(() => {
    if (!uid || state !== "searching") return undefined;

    const goToGame = (gameID) => {
      if (navigatedRef.current) return;
      navigatedRef.current = true;
      navigate(`/battles/pickup/draft?gameID=${gameID}`);
    };

    // Primary: the queue entry flips to "matched" with the new game's id.
    const unsubEntry = listenToMatchmakingEntry({ userID: uid, sportId }, (entry) => {
      if (!entry) return;
      if (entry.status === "matched" && entry.matchedGameID) goToGame(entry.matchedGameID);
      else if (entry.status === "expired") setState("expired");
    });

    // Fallback: a brand-new active game for this sport appears in the games collection — route
    // straight in even if the entry update is delayed. Isolated so a problem here is harmless.
    // The listener emits a few times as its queries settle, so everything seen in the first
    // moment is treated as pre-existing; only a genuinely new game id triggers navigation.
    let unsubGames = null;
    try {
      if (typeof listenToActivePickUpGames === "function") {
        const seen = new Set();
        const settleUntil = Date.now() + 1500;
        unsubGames = listenToActivePickUpGames(uid, (games) => {
          const mine = games.filter((g) => g.sport === sportId && g.status === "active");
          if (Date.now() < settleUntil) {
            mine.forEach((g) => seen.add(g.id)); // baseline window
            return;
          }
          const fresh = mine.find((g) => !seen.has(g.id));
          mine.forEach((g) => seen.add(g.id));
          if (fresh) goToGame(fresh.id);
        });
      }
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("matchmaking: game-detection fallback unavailable", e);
      }
    }

    return () => {
      if (unsubEntry) unsubEntry();
      if (unsubGames) unsubGames();
    };
  }, [uid, sportId, state, navigate]);

  // Countdown ticker.
  useEffect(() => {
    if (state !== "searching") return undefined;
    const t = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [state]);

  const cancel = useCallback(async () => {
    navigatedRef.current = true; // block any late auto-navigation during cancel
    try {
      await cancelMatchmaking({ userID: uid, sportId });
    } catch (_) {
      /* ignore */
    }
    navigate("/battles/pickup");
  }, [uid, sportId, navigate]);

  return (
    <div style={styles.page}>
      <style>{`@keyframes pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.12); opacity: 0.65; } }`}</style>
      <div style={styles.header}>
        <button style={styles.iconBtn} onClick={() => navigate("/battles/pickup")}>‹</button>
      </div>

      <div style={styles.center}>
        {(state === "starting" || state === "searching") && (
          <>
            <div style={{ ...styles.pulse, borderColor: meta.hex }}>
              <div style={{ ...styles.pulseCore, background: meta.hex }} />
            </div>
            <h2 style={styles.heading}>Finding a {meta.label} opponent…</h2>
            <p style={styles.countdown}>{fmtRemaining(expiresAt)}</p>
            <p style={styles.sub}>
              We'll match you with another player in the queue. You can leave this screen — the
              search keeps running until you cancel.
            </p>
            <button style={styles.cancelBtn} onClick={cancel}>CANCEL SEARCH</button>
          </>
        )}

        {state === "expired" && (
          <>
            <h2 style={styles.heading}>Search expired</h2>
            <p style={styles.sub}>No opponent found. Try again whenever you're ready.</p>
            <button style={{ ...styles.primaryBtn, background: meta.hex }} onClick={enterQueue}>
              SEARCH AGAIN
            </button>
            <button style={styles.cancelBtn} onClick={() => navigate("/battles/pickup")}>BACK</button>
          </>
        )}

        {state === "error" && (
          <>
            <h2 style={styles.heading}>Couldn't start matchmaking</h2>
            <button style={{ ...styles.primaryBtn, background: meta.hex }} onClick={enterQueue}>RETRY</button>
            <button style={styles.cancelBtn} onClick={() => navigate("/battles/pickup")}>BACK</button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { padding: "16px 16px 96px", maxWidth: 560, margin: "0 auto", color: "#e8e9ec", minHeight: "100vh" },
  header: { display: "flex", alignItems: "center", marginBottom: 16 },
  iconBtn: { background: "none", border: "none", color: "rgba(255,255,255,0.8)", fontSize: 28, cursor: "pointer", width: 32, lineHeight: 1 },
  center: { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", marginTop: 60, gap: 14 },
  pulse: { width: 96, height: 96, borderRadius: 48, border: "3px solid", display: "flex", alignItems: "center", justifyContent: "center", animation: "pulse 1.4s ease-in-out infinite" },
  pulseCore: { width: 40, height: 40, borderRadius: 20 },
  heading: { fontSize: 20, fontWeight: 800, margin: "8px 0 0" },
  countdown: { fontFamily: "monospace", fontSize: 28, fontWeight: 700, color: "#c4c8cf", margin: 0 },
  sub: { fontSize: 13, color: "#9aa0aa", lineHeight: 1.5, maxWidth: 340 },
  cancelBtn: { marginTop: 8, padding: "12px 24px", borderRadius: 10, border: "1px solid #3a3e48", background: "transparent", color: "#c4c8cf", fontWeight: 700, letterSpacing: 0.5, cursor: "pointer" },
  primaryBtn: { padding: "12px 28px", borderRadius: 10, border: "none", color: "#0c0d10", fontWeight: 800, letterSpacing: 1, cursor: "pointer" },
};
